/**
 * features/hr/LeaveEncashments.tsx
 * ------------------------------------------------------------------
 * SOLD LEAVE — a register, not a workflow.
 *
 * It records that a member of staff sold leave days, who recorded it and
 * who approved it, and takes the days off the balance. That is all it does.
 *
 * NO MONEY APPEARS HERE, by design. What is paid, how it is worked out and
 * who authorises it are handled by the finance office outside this system.
 * A figure shown here would be read as what the school owes, and this
 * system would not be the one that knows.
 *
 * ONE PERSON MAY RECORD AND APPROVE. The school asked for both names to be
 * captured, not for the system to police who they are.
 *
 * EVERY REFUSAL IS SAID BEFORE THE BUTTON IS PRESSED — not enough balance,
 * or an approval that would move unpaid leave already charged. A button
 * that looks available and then fails teaches people to stop trusting the
 * screen.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  ClipboardList, Plus, Check, X, Ban, Hourglass, CircleCheck, Info, Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useHrData } from '@/features/hr/useHrData';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import StaffPicker from '@/features/hr/StaffPicker';
import { staffFullName } from '@/lib/hr';
import { leaveBalancesFor, leaveBasis, currentLeaveYear } from '@/lib/leavePolicy';
import {
  salesFor, requestProblem, approvalProblem, cancelProblem, unpaidChanges, fmtDays,
  ENCASHMENT_STATUS_LABEL, FINANCE_OFFICE_NOTE,
  type HrLeaveEncashment,
} from '@/lib/leaveEncashment';
import {
  recordEncashment, approveEncashment, rejectEncashment, cancelEncashment,
} from '@/features/hr/encashmentWrite';
import {
  SoftCard, StatChip, PanelHeader, AccentButton, glassFieldClass,
} from '@/features/finance/ui';

type Filter = 'pending' | 'approved' | 'all';
type Acting = { id: string; mode: 'reject' | 'cancel' } | null;

export default function LeaveEncashments({
  data,
  canApprove,
  onOpenStaff,
}: {
  data: ReturnType<typeof useHrData>;
  canApprove: boolean;
  onOpenStaff: (staffId: string) => void;
}) {
  const { userData } = useAuth();
  const { policies } = useLeavePolicies();
  const [filter, setFilter] = useState<Filter>('pending');
  const [recording, setRecording] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState<Acting>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const actor = {
    uid: userData?.uid,
    email: userData?.email,
    displayName: userData?.displayName,
    role: userData?.role,
  };

  const sales = data.leaveEncashments;

  /** Annual balance for one person, sales included — the same engine and
   *  inputs as the leave screens, so this can never disagree with them. */
  function annualFor(sid: string, withSales = salesFor(sales, sid)) {
    const staff = data.staff.find((s) => s.id === sid);
    if (!staff) return 0;
    const b = leaveBalancesFor({
      category: staff.category,
      allocation: data.leaveAllocations.find((a) => a.staffId === sid),
      requests: data.leave.filter((l) => l.staffId === sid),
      policies,
      year: currentLeaveYear(),
      accrualStart: data.payrollSettings?.leaveAccrualStart,
      employmentStart: staff.startDate,
      encashments: withSales,
    });
    return b.find((x) => x.type === 'annual')?.remaining ?? 0;
  }

  /** Approved leave whose unpaid days would move if this sale's status
   *  changed to `to` — the school's existing unpaid-leave rule, nothing to
   *  do with paying for the sale itself. */
  function knockOn(e: HrLeaveEncashment, to: HrLeaveEncashment['status']): string[] {
    const mine = sales.filter((x) => x.staffId === e.staffId);
    const before = salesFor(mine, e.staffId);
    const after = salesFor(mine.map((x) => (x.id === e.id ? { ...x, status: to, decidedAt: x.decidedAt ?? new Date().toISOString() } : x)), e.staffId);
    const staff = data.staff.find((s) => s.id === e.staffId);
    const basis = leaveBasis({
      category: staff?.category, allocation: data.leaveAllocations.find((a) => a.staffId === e.staffId), policies,
      year: currentLeaveYear(), accrualStart: data.payrollSettings?.leaveAccrualStart, employmentStart: staff?.startDate,
    });
    return unpaidChanges(data.leave.filter((l) => l.staffId === e.staffId), basis.allocation, before, after, basis.opts);
  }

  const counts = useMemo(() => {
    const pending = sales.filter((s) => s.status === 'pending');
    const approved = sales.filter((s) => s.status === 'approved');
    return {
      pending: pending.length,
      approved: approved.length,
      approvedDays: approved.reduce((n, s) => n + (Number(s.days) || 0), 0),
    };
  }, [sales]);

  const shown = useMemo(() => [...sales]
    .filter((s) => (filter === 'all' ? true : s.status === filter))
    .sort((a, b) => String(b.requestedAt).localeCompare(String(a.requestedAt))), [sales, filter]);

  // --- the recording form -------------------------------------------------
  const chosen = data.staff.find((s) => s.id === staffId);
  const available = staffId ? annualFor(staffId) : 0;
  const dayNum = Number(days);
  const formProblem = staffId && days
    ? requestProblem({ staffStatus: chosen?.status, type: 'annual', days: dayNum, available })
    : null;

  async function submit() {
    if (!chosen) return;
    setBusy('record'); setMsg(null);
    try {
      await recordEncashment({
        staffId: chosen.id, staffName: staffFullName(chosen), staffStatus: chosen.status,
        days: dayNum, reason, available, actor,
      });
      setMsg({ ok: true, text: `${fmtDays(dayNum)} recorded for ${staffFullName(chosen)}, awaiting approval.` });
      setStaffId(''); setDays(''); setReason(''); setRecording(false);
      setFilter('pending');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(''); }
  }

  async function approve(e: HrLeaveEncashment) {
    setBusy(e.id); setMsg(null);
    try {
      await approveEncashment({
        encashment: e, available: annualFor(e.staffId), unpaidChanges: knockOn(e, 'approved'), actor,
      });
      setMsg({ ok: true, text: `Approved. ${fmtDays(e.days)} taken off ${e.staffName ?? 'their'} balance.` });
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally { setBusy(''); }
  }

  async function confirmActing(e: HrLeaveEncashment) {
    if (!acting) return;
    setBusy(e.id); setMsg(null);
    try {
      if (acting.mode === 'reject') {
        await rejectEncashment({ encashment: e, note, actor });
        setMsg({ ok: true, text: 'Rejected. Nothing came off the balance.' });
      } else {
        await cancelEncashment({ encashment: e, reason: note, unpaidChanges: knockOn(e, 'cancelled'), actor });
        setMsg({ ok: true, text: e.status === 'approved'
          ? `Cancelled. ${fmtDays(e.days)} returned to the balance.`
          : 'Cancelled.' });
      }
      setActing(null); setNote('');
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally { setBusy(''); }
  }

  return (
    <div className="space-y-4">
      <SoftCard className="border-l-4 border-[#1b3a6b]">
        <p className="text-sm text-gray-700 dark:text-gray-200">
          <Info size={14} className="inline mr-1 -mt-0.5" />
          This is a <b>record of leave days sold</b> — the days, who recorded them and who
          approved them. Approval takes the days off the leave balance. {FINANCE_OFFICE_NOTE}
        </p>
      </SoftCard>

      {msg && (
        <SoftCard className={`border-l-4 ${msg.ok ? 'border-emerald-500' : 'border-red-500'}`}>
          <p className={`text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {msg.text}
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="gold" icon={Hourglass} value={counts.pending} label="Awaiting approval" />
        <StatChip tone="emerald" icon={CircleCheck} value={counts.approved} label="Approved sales" />
        <StatChip tone="navy" icon={ClipboardList} value={fmtDays(counts.approvedDays)}
          label="Days sold and approved" />
      </div>

      {canApprove && !recording && (
        <AccentButton icon={Plus} onClick={() => { setRecording(true); setMsg(null); }}>
          Record a sale of leave
        </AccentButton>
      )}

      {recording && (
        <SoftCard pad="p-0">
          <PanelHeader icon={ClipboardList} tone="navy" title="Record a sale of leave"
            subtitle="Annual leave only. The days come off the balance once approved." />
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Member of staff</label>
              <StaffPicker staff={data.staff} value={staffId} onChange={setStaffId} activeOnly />
            </div>

            {chosen && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Annual leave available to sell: <b>{fmtDays(Math.max(0, available))}</b>
                {dayNum > 0 && !formProblem && (
                  <> · balance after this sale: <b>{fmtDays(available - dayNum)}</b></>
                )}
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Days to sell</label>
                <input type="number" min={0.5} step={0.5} value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className={glassFieldClass('w-full')} placeholder="e.g. 5" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
                <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
                  className={glassFieldClass('w-full')}
                  placeholder="e.g. Staff request dated 12 Sep, signed form on file" />
              </div>
            </div>

            {formProblem && (
              <p className="text-sm text-red-700 dark:text-red-300">{formProblem}</p>
            )}

            <div className="flex gap-2">
              <AccentButton icon={busy === 'record' ? Loader2 : Check} onClick={submit}
                disabled={!chosen || !days || !!formProblem || reason.trim().length < 3 || busy === 'record'}>
                Record for approval
              </AccentButton>
              <AccentButton variant="soft" onClick={() => { setRecording(false); setStaffId(''); setDays(''); setReason(''); }}>
                Cancel
              </AccentButton>
            </div>
          </div>
        </SoftCard>
      )}

      <SoftCard pad="p-0">
        <PanelHeader icon={ClipboardList} tone="navy" title="Sold leave"
          subtitle="Newest first"
          right={(
            <select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}
              className={glassFieldClass('text-xs max-w-[180px]')}>
              <option value="pending">Awaiting approval ({counts.pending})</option>
              <option value="approved">Approved ({counts.approved})</option>
              <option value="all">Everything ({sales.length})</option>
            </select>
          )}
        />

        {shown.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            {sales.length === 0 ? 'No leave has been sold yet.' : 'Nothing in this list.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Staff</th>
                  <th className="px-2 font-semibold text-right">Days</th>
                  <th className="px-2 font-semibold">Reason</th>
                  <th className="px-2 font-semibold">Recorded</th>
                  <th className="px-2 font-semibold">Status</th>
                  <th className="px-2 pr-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => {
                  const approveBlock = e.status === 'pending'
                    ? approvalProblem({
                      encashment: e,
                      available: annualFor(e.staffId), unpaidChanges: knockOn(e, 'approved'),
                    })
                    : null;
                  const isActing = acting?.id === e.id;
                  return (
                    <tr key={e.id} className="border-b border-gray-50 dark:border-gray-800 align-top">
                      <td className="py-2.5 pl-4 pr-2">
                        <button onClick={() => onOpenStaff(e.staffId)}
                          className="font-medium text-primaryLight hover:underline text-left">
                          {e.staffName || e.staffId}
                        </button>
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums font-medium">{e.days}</td>
                      <td className="px-2 py-2.5 text-gray-600 dark:text-gray-300 max-w-[260px]">
                        {e.reason}
                        {e.decisionNote && (
                          <span className="block text-[11px] text-gray-400 mt-0.5">Note: {e.decisionNote}</span>
                        )}
                        {e.cancelReason && (
                          <span className="block text-[11px] text-gray-400 mt-0.5">Cancelled: {e.cancelReason}</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-xs text-gray-500">
                        {String(e.requestedAt).slice(0, 10)}
                        <span className="block text-[11px] text-gray-400">{e.requestedByName}</span>
                      </td>
                      <td className="px-2 py-2.5 text-xs">
                        <span className={
                          e.status === 'approved' ? 'text-emerald-700 dark:text-emerald-300 font-medium'
                            : e.status === 'pending' ? 'text-amber-700 dark:text-amber-300 font-medium'
                              : 'text-gray-500'
                        }>
                          {ENCASHMENT_STATUS_LABEL[e.status]}
                        </span>
                        {e.decidedByName && (
                          <span className="block text-[11px] text-gray-500">
                            by {e.decidedByName}
                            {e.decidedAt ? ` · ${String(e.decidedAt).slice(0, 10)}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-2 pr-4 py-2.5 text-right">
                        {canApprove && e.status === 'pending' && !isActing && (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex gap-1.5">
                              <button onClick={() => approve(e)} disabled={!!approveBlock || busy === e.id}
                                title={approveBlock ?? undefined}
                                className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
                                <Check size={12} className="inline mr-1" />Approve
                              </button>
                              <button onClick={() => { setActing({ id: e.id, mode: 'reject' }); setNote(''); }}
                                disabled={busy === e.id}
                                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800">
                                <X size={12} className="inline mr-1" />Reject
                              </button>
                            </div>
                            {approveBlock && (
                              <span className="text-[11px] text-gray-500 max-w-[240px]">{approveBlock}</span>
                            )}
                          </div>
                        )}
                        {canApprove && e.status === 'approved' && !isActing && (
                          <button onClick={() => { setActing({ id: e.id, mode: 'cancel' }); setNote(''); }}
                            disabled={busy === e.id}
                            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800">
                            <Ban size={12} className="inline mr-1" />Cancel sale
                          </button>
                        )}
                        {isActing && (
                          <div className="flex flex-col items-end gap-1.5 min-w-[240px]">
                            <input value={note} onChange={(ev) => setNote(ev.target.value)} autoFocus
                              placeholder={acting?.mode === 'reject' ? 'Why is it rejected?' : 'Why is it cancelled?'}
                              className={glassFieldClass('w-full text-xs')} />
                            {acting?.mode === 'cancel' && e.status === 'approved' && (() => {
                              const why = cancelProblem({ encashment: e, reason: note || 'xxx', unpaidChanges: knockOn(e, 'cancelled') });
                              return why ? <span className="text-[11px] text-red-600">{why}</span> : null;
                            })()}
                            <div className="flex gap-1.5">
                              <button onClick={() => confirmActing(e)} disabled={note.trim().length < 3 || busy === e.id}
                                className="text-xs px-2.5 py-1.5 rounded-lg bg-[#1b3a6b] text-white disabled:opacity-40">
                                {acting?.mode === 'reject' ? 'Reject' : 'Cancel sale'}
                              </button>
                              <button onClick={() => { setActing(null); setNote(''); }}
                                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600">
                                Back
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>
    </div>
  );
}
